/**
 * AgentChatHostContext — 앱 셸에 **단 하나** 존재하는 Agent Chat 패널과
 * 트리 아래 화면(시나리오 플래닝 등) 사이의 배선.
 *
 * 배경: 예전에는 챗 패널이 3곳(App 홈 분기 / TabContainer 탭 분기 / 시나리오 화면)에서 각각
 *   마운트됐고, 화면을 옮길 때마다 진짜로 언마운트→재마운트되면서 대화를 redux 스냅샷으로
 *   저장·복원해 "이어지는 것처럼" 보이게 했다(스크롤 튐·입력 소실의 원인).
 *   이제 패널은 앱 셸에 하나만 살아 있고, **화면이 자신의 컨텍스트를 이 레지스트리에 publish** 한다.
 *
 * 컨텍스트를 3채널로 쪼갠 이유: 화면(publisher)이 패널 상태 변화에 재렌더되지 않게 하기 위함.
 *   PublishCtx / ApiCtx / ApiRefCtx 의 value 는 identity 가 영구 고정이고,
 *   자주 바뀌는 값은 ValueCtx 하나에만 담아 호스트만 구독한다.
 *
 * 등록 규약은 AgentActions.js 의 useProvideAgentActions 와 동일하다 —
 *   factory + 명시 deps. 매 렌더 새로 만들어지는 객체를 deps 에 넣으면
 *   publish → 재렌더 → publish 무한루프가 난다(AiScenarioPlanning.js 의 같은 함정 주석 참조).
 */
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

const PublishCtx = createContext(null);   // { publish, retract } — 화면이 사용
const ApiCtx = createContext(null);       // { notify, promptChoices } — 화면이 사용
const ApiRefCtx = createContext(null);    // 패널에 붙일 ref — 호스트가 사용
const ValueCtx = createContext(null);     // 현재 화면 컨텍스트 | null — 호스트가 구독

export function AgentChatHostProvider({ children }) {
  const scopes = useRef(new Map());
  const [screen, setScreen] = useState(null);
  const apiRef = useRef(null);

  // 여러 화면이 동시에 마운트돼 있을 수 있다(탭 구조). **가장 마지막에 publish 된 것**을 쓴다.
  //   Map 은 삽입 순서를 유지하므로 마지막 엔트리가 곧 최근 등록이다.
  const recompute = useCallback(() => {
    let last = null;
    for (const v of scopes.current.values()) last = v;
    setScreen(last ?? null);
  }, []);

  const publish = useCallback((scopeId, ctx) => {
    // 재등록 시 순서를 갱신해야 '마지막 publish 우선' 규칙이 유지된다(delete 후 set).
    scopes.current.delete(scopeId);
    scopes.current.set(scopeId, ctx);
    recompute();
  }, [recompute]);

  const retract = useCallback((scopeId) => {
    if (scopes.current.delete(scopeId)) recompute();
  }, [recompute]);

  const publishApi = useMemo(() => ({ publish, retract }), [publish, retract]);

  // 화면 → 패널 명령형 호출. 예전엔 화면이 패널의 ref 를 직접 들고 있었지만(패널이 화면의
  //   자식이었으므로), 이제 패널이 트리 위쪽에 있으므로 방향을 뒤집는다.
  //   래퍼의 identity 는 영구 고정이라 화면의 useCallback deps 를 오염시키지 않는다.
  const api = useMemo(() => ({
    notify: (...args) => apiRef.current?.notify?.(...args),
    promptChoices: (...args) => apiRef.current?.promptChoices?.(...args),
  }), []);

  return (
    <PublishCtx.Provider value={publishApi}>
      <ApiCtx.Provider value={api}>
        <ApiRefCtx.Provider value={apiRef}>
          <ValueCtx.Provider value={screen}>{children}</ValueCtx.Provider>
        </ApiRefCtx.Provider>
      </ApiCtx.Provider>
    </PublishCtx.Provider>
  );
}

/**
 * 화면이 "지금 자신이 제공하는 챗 컨텍스트"를 등록한다.
 *   useLayoutEffect 인 이유: useEffect 면 화면 전환 후 한 프레임 동안 패널이 standalone 모드로
 *   보여(칩이 '복귀 제안'으로) 깜빡인다.
 */
export function useProvideAgentChat(scopeId, factory, deps) {
  const ctx = useContext(PublishCtx);
  useLayoutEffect(() => {
    if (!ctx) return undefined;
    ctx.publish(scopeId, typeof factory === "function" ? factory() : factory);
    return () => ctx.retract(scopeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, ...(deps ?? [])]);
}

// Provider 밖(챗이 없는 렌더 경로)에서도 호출부가 터지지 않게 하는 무동작 폴백.
const NOOP_API = { notify: () => {}, promptChoices: () => {} };

/** 화면: 패널에 메시지·선택지를 밀어 넣는다(비동기 작업 완료 알림 등). */
export function useAgentChatApi() {
  return useContext(ApiCtx) ?? NOOP_API;
}

/** 호스트: 현재 화면 컨텍스트(없으면 null = 챗 미배선 화면). */
export function useAgentChatScreen() {
  return useContext(ValueCtx);
}

/** 호스트: 패널에 붙일 ref. */
export function useAgentChatApiRef() {
  return useContext(ApiRefCtx);
}
